import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle, Mail, Shield, User, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const { handleAuthCallback } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Получаем параметры из URL и hash
        const urlType = params.get('type');
        const token = params.get('token');
        const code = params.get('code');
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        const errorCode = params.get('error_code');

        // Также проверяем hash параметры (иногда Supabase использует hash)
        const hash = location.hash;
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        const hashError = hashParams.get('error');
        const hashErrorDescription = hashParams.get('error_description');
        const hashErrorCode = hashParams.get('error_code');
        const hashType = hashParams.get('type');
        const hashCode = hashParams.get('code');
        const hashToken = hashParams.get('token');

        console.log('Auth callback params:', { 
          urlType, token, code, error, errorDescription, errorCode,
          hashError, hashErrorDescription, hashErrorCode, hashType, hashCode, hashToken,
          fullUrl: window.location.href,
          search: location.search,
          hash: location.hash
        });

        const finalType = urlType || hashType;
        const emailLinkTypes = ['signup', 'recovery', 'email_change', 'invite'];
        const isEmailLinkType = finalType && emailLinkTypes.includes(finalType);
        const finalError = error || hashError;
        const finalErrorDescription = errorDescription || hashErrorDescription;
        const finalErrorCode = errorCode || hashErrorCode;
        const finalCode = code || hashCode;
        const finalToken = token || hashToken;

        setType(isEmailLinkType ? finalType : '');

        // Проверяем, есть ли вообще какие-то параметры
        const hasAnyParams = finalType || finalToken || finalCode || finalError || finalErrorCode;
        
        if (!hasAnyParams) {
          console.log('No auth parameters found, redirecting to landing');
          setStatus('success');
          setMessage('Перанакіраванне на галоўную старонку...');
          setTimeout(() => navigate('/'), 1000);
          return;
        }

        // Специальная обработка для otp_expired - это нормальная ситуация
        if (finalErrorCode === 'otp_expired' || finalError === 'access_denied') {
          console.log('OTP expired or access denied - treating as success for email verification');
          
          // Если есть тип операции, значит пользователь пришёл по email ссылке
          if (isEmailLinkType) {
            const successMessage = getSuccessMessage(finalType);
            setMessage(successMessage);
            setStatus('success');
            toast.success(successMessage);

            // Редирект в зависимости от типа
            setTimeout(() => {
              if (finalType === 'recovery') {
                navigate('/auth/reset-password');
              } else {
                navigate('/');
              }
            }, finalType === 'recovery' ? 1500 : 3000);
            
            return;
          } else {
            // Если нет типа, но есть otp_expired - показываем общий успех
            console.log('OTP expired without type - showing general success');
            setStatus('success');
            setMessage('Email паспяхова пацверджаны!');
            toast.success('Email паспяхова пацверджаны!');
            setTimeout(() => navigate('/'), 3000);
            return;
          }
        }

        // Проверяем на другие ошибки в URL
        if (finalError && finalErrorCode !== 'otp_expired') {
          console.error('Auth callback error:', finalError, finalErrorDescription);
          setStatus('error');
          setMessage(getErrorMessage(finalError, finalErrorDescription));
          toast.error(getErrorMessage(finalError, finalErrorDescription));
          setTimeout(() => navigate('/'), 4000);
          return;
        }

        // Если есть тип операции - значит пользователь пришёл по email ссылке
        if (isEmailLinkType) {
          console.log('Processing email verification for type:', finalType);
          
          // Для email операций не пытаемся обменивать код - просто показываем успех
          // Supabase уже обработал верификацию на своей стороне
          const successMessage = getSuccessMessage(finalType);
          setMessage(successMessage);
          setStatus('success');
          toast.success(successMessage);

          // Проверяем текущую сессию без попытки обмена кода
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            console.log('Current session after email verification:', {
              hasSession: !!sessionData.session,
              userEmail: sessionData.session?.user?.email,
              emailConfirmed: sessionData.session?.user?.email_confirmed_at
            });
            
            if (sessionData.session?.user?.email_confirmed_at) {
              await handleAuthCallback();
            }
          } catch (sessionError) {
            console.log('Session check failed (this is normal for email verification):', sessionError);
            // Продолжаем - покажем успех в любом случае
          }

          // Редирект в зависимости от типа
          setTimeout(() => {
            if (finalType === 'recovery') {
              navigate('/auth/reset-password');
            } else {
              navigate('/');
            }
          }, finalType === 'recovery' ? 1500 : 3000);
          
          return;
        }

        // Если нет типа, но есть код/токен - это OAuth или другой flow
        if (finalCode || finalToken) {
          console.log('Processing OAuth callback...');

          try {
            if (finalCode) {
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(finalCode);
              if (exchangeError) {
                console.log('Code exchange error:', exchangeError);
                const { data: existing } = await supabase.auth.getSession();
                if (existing.session?.user && exchangeError.message?.includes('code_verifier')) {
                  await handleAuthCallback();
                  navigate('/');
                  return;
                }
                throw exchangeError;
              }
            }

            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData.session?.user) {
              await handleAuthCallback();
              navigate('/');
              return;
            }

            setStatus('error');
            setMessage('Не атрымалася завяршыць ўваход праз Google. Паспрабуйце яшчэ раз.');
            toast.error('Не атрымалася завяршыць уваход праз Google. Паспрабуйце яшчэ раз.');
            setTimeout(() => navigate('/'), 4000);
            return;
          } catch (authError) {
            console.error('Auth processing failed:', authError);

            const { data: sessionData } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
            if (sessionData.session?.user) {
              await handleAuthCallback();
              navigate('/');
              return;
            }

            setStatus('error');
            setMessage('Не атрымалася завяршыць уваход праз Google. Паспрабуйце яшчэ раз.');
            toast.error('Не атрымалася завяршыць уваход праз Google. Паспрабуйце яшчэ раз.');
            setTimeout(() => navigate('/'), 4000);
            return;
          }
        }

        // Если дошли до сюда и нет никаких параметров - просто редиректим
        console.log('No specific parameters found, redirecting to landing');
        setStatus('success');
        setMessage('Перанакіраванне на галоўную старонку...');
        setTimeout(() => navigate('/'), 1000);

      } catch (error) {
        console.error('Auth callback processing error:', error);
        setStatus('error');
        
        let errorMessage = 'Адбылася памылка пры аутентификации';
        
        if (error instanceof Error) {
          // Более дружелюбные сообщения для известных ошибок
          if (error.message.includes('expired')) {
            errorMessage = 'Спасылка састарэла, але ваш email ужо можа быць пацверджаны. Паспрабуйце ўвайсці ў сістэму.';
          } else if (error.message.includes('invalid')) {
            errorMessage = 'Няправільная спасылка. Паспрабуйце запытаць новую спасылку для пацверджання.';
          } else if (error.message.includes('code verifier')) {
            errorMessage = 'Не атрымалася завяршыць ўваход праз Google. Паспрабуйце яшчэ раз.';
          } else {
            errorMessage = error.message;
          }
        }
        
        setMessage(errorMessage);
        toast.error(errorMessage);
        
        // Редирект на главную через 4 секунды
        setTimeout(() => navigate('/'), 4000);
      }
    };

    // Добавляем небольшую задержку для избежания конфликтов
    const timer = setTimeout(processCallback, 100);
    
    return () => clearTimeout(timer);
  }, [params, location.hash, handleAuthCallback, navigate]);

  const getSuccessMessage = (type: string | null): string => {
    switch (type) {
      case 'signup':
        return 'Ваш email паспяхова пацверджаны! Цяпер вы можаце карыстацца ўсімі функцыямі сайта.';
      case 'recovery':
        return 'Спасылка для скідвання пароля пацверджана. Цяпер вы можаце ўсталяваць новы пароль.';
      case 'email_change':
        return 'Ваш новы email адрас паспяхова пацверджаны.';
      case 'invite':
        return 'Запрашэнне прынята! Ласкава запрашаем.';
      default:
        return 'Email паспяхова пацверджаны!';
    }
  };

  const getErrorMessage = (error: string | null, description: string | null): string => {
    if (description) {
      return `Памылка аутентификации: ${description}`;
    }
    
    switch (error) {
      case 'access_denied':
        return 'Доступ адхілены. Калі ласка, паспрабуйце яшчэ раз.';
      case 'invalid_request':
        return 'Няправільны запыт. Спасылка могла састарэць.';
      case 'unauthorized_client':
        return 'Несанкцыянаваны кліент.';
      case 'unsupported_response_type':
        return 'Непадтрымліваемы тып адказу.';
      case 'invalid_scope':
        return 'Няправільная вобласць доступу.';
      case 'server_error':
        return 'Памылка сервера. Калі ласка, паспрабуйце пазней.';
      case 'temporarily_unavailable':
        return 'Сэрвіс часова недаступны. Калі ласка, паспрабуйце пазней.';
      default:
        return 'Адбылася памылка пры аутентификации. Калі ласка, паспрабуйце яшчэ раз.';
    }
  };

  const getIcon = () => {
    if (status === 'loading') {
      return <Loader2 className="w-16 h-16 text-primary animate-spin" />;
    }
    
    if (status === 'error') {
      return <AlertCircle className="w-16 h-16 text-red-500" />;
    }

    // Иконка в зависимости от типа успешной операции
    switch (type) {
      case 'signup':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'recovery':
        return <Shield className="w-16 h-16 text-blue-500" />;
      case 'email_change':
        return <Mail className="w-16 h-16 text-purple-500" />;
      case 'invite':
        return <User className="w-16 h-16 text-indigo-500" />;
      default:
        return <CheckCircle className="w-16 h-16 text-green-500" />;
    }
  };

  const getTitle = () => {
    if (status === 'loading') {
      return 'Апрацоўка...';
    }
    
    if (status === 'error') {
      return 'Памылка';
    }

    switch (type) {
      case 'signup':
        return 'Email пацверджаны!';
      case 'recovery':
        return 'Скід пароля';
      case 'email_change':
        return 'Email зменены';
      case 'invite':
        return 'Запрашэнне прынята';
      default:
        return 'Паспяхова!';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
        
        {/* Иконка */}
        <div className="flex justify-center mb-6">
          {getIcon()}
        </div>

        {/* Заголовок */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {getTitle()}
        </h1>

        {/* Сообщение */}
        <p className={`text-base leading-relaxed mb-6 ${
          status === 'error' ? 'text-red-600' : 'text-gray-700'
        }`}>
          {message}
        </p>

        {/* Индикатор загрузки */}
        {status === 'loading' && (
          <div className="space-y-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
            <p className="text-sm text-gray-500">
              Праверка аутентификации...
            </p>
          </div>
        )}

        {/* Кнопка для ручного редиректа */}
        {status !== 'loading' && (
          <div className="space-y-3">
            <button
              onClick={() => {
                if (type === 'recovery') {
                  navigate('/auth/reset-password');
                } else {
                  navigate('/');
                }
              }}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                status === 'error'
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-primary hover:bg-primary/90 text-white'
              }`}
            >
              {type === 'recovery' ? 'Усталяваць новы пароль' : 'Перайсці на галоўную'}
            </button>
            
            {status === 'success' && type !== 'recovery' && (
              <p className="text-xs text-gray-500">
                Аўтаматычны редырэкт праз некалькі секунд...
              </p>
            )}
          </div>
        )}

        {/* Дополнительная информация для успешных операций */}
        {status === 'success' && (
          <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200">
            <div className="text-sm text-green-700">
              {type === 'signup' && (
                <div>
                  <p className="font-medium mb-2">Што далей?</p>
                  <ul className="text-left space-y-1">
                    <li>• Запоўніце свой профіль</li>
                    <li>• Дадайце лакацыі ў абранае</li>
                    <li>• Пачніце дзяліцца сваімі месцамі</li>
                  </ul>
                </div>
              )}
              
              {type === 'recovery' && (
                <p>Цяпер вы можаце ўсталяваць новы пароль для вашага акаўнта.</p>
              )}
              
              {type === 'email_change' && (
                <p>Ваш новы email адрас цяпер актыўны і можа выкарыстоўвацца для ўваходу.</p>
              )}

              {!type && (
                <p>Ваш email паспяхова пацверджаны і вы можаце карыстацца ўсімі функцыямі сайта.</p>
              )}
            </div>
          </div>
        )}

        {/* Дополнительная помощь при ошибке */}
        {status === 'error' && (
          <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="text-sm text-red-700">
              <p className="font-medium mb-2">Што рабіць?</p>
              <ul className="text-left space-y-1">
                <li>• Паспрабуйце ўвайсці ў сістэму звычайным спосабам</li>
                <li>• Запытайце новую спасылку для пацверджання</li>
                <li>• Звярніцеся ў падтрымку па адрасе mamanovichks@gmail.com</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;